import { useMemo, useState } from "react";
import {
  AreaChart, Area, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine
} from "recharts";
import { useWell } from "../WellContext";           
import { useAIAdvice } from "../useAIAdvice";
import { useTheme, getColors } from "../ThemeContext";   // <-- added
import ForecastPanel from "../components/ForecastPanel";
import PlotSummary from "../components/PlotSummary";

const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",lime:"#b0ff4a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }   // <-- added

const CHART_MARGIN_DEFAULT  = { top: 8, right: 14, left: -10, bottom: 6 };   // two-row X axis (cycle + timestamp) now supplies the bottom space
const CHART_MARGIN_DUAL_Y   = { top: 8, right: 14, left: -10, bottom: 6 };
const CHART_MARGIN_SCATTER  = { top: 8, right: 14, left: -10, bottom: 36 };

const LEGEND_PROPS = {
  wrapperStyle: { fontFamily: MONO, fontSize: 10, paddingBottom: 4 },
  verticalAlign: "top", align: "right", iconSize: 8,
};

function cycleTickFmt(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!isNaN(n)) return `C${n}`;
  if (String(v).includes(":") || String(v).includes("-")) return String(v).slice(0, 8);
  return String(v);
}

/* Top row of the X axis = cycle number (unchanged). interval/minTickGap thin the labels so they never collide. */
function CYCLE_XAXIS_PROPS() {
  return {
    tick: { fill: C.muted, fontSize: 9, fontFamily: MONO },
    tickLine: false,
    tickFormatter: cycleTickFmt,
    interval: "preserveStartEnd",
    minTickGap: 26,
    height: 20,
  };
}

/* ───────────── TIME-WINDOW HELPERS ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" · parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

function statsOf(rows, col) {
  const v = rows.map(r => r[col]).filter(x => typeof x === "number" && !isNaN(x));
  if (!v.length) return null;
  const n = v.length, mean = v.reduce((a, b) => a + b, 0) / n;
  const s = [...v].sort((a, b) => a - b);
  const median = n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
  const std = n > 1 ? Math.sqrt(v.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1)) : 0;
  return { mean, min: s[0], max: s[n - 1], std, median, count: n };
}

/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) · recharts only recognises <XAxis> as a direct child. */
function dateXAxis(rows, nTicks = 5) {
  const last = rows.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => rows[i]).map(i => rows[i].index);
  const lookup = new Map(rows.map(r => [r.index, r._t]));
  const span = rows.length > 1 ? (rows[last]._t - rows[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey="index" ticks={ticks} interval={0} tickLine={false} axisLine={false} height={32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}/>
  );
}

function AnimNum({ to, dec = 1, suffix = "" }) {
  if (to == null || isNaN(Number(to))) return <span>·</span>;
  return <span>{Number(to).toFixed(dec)}{suffix}</span>;
}

function StatCard({ label, value, unit, color, sub, icon, delay = 0, context, windowLabel }) {
  return (
    <div className="card-hover" style={{
      background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${color}`,
      borderRadius: 12, padding: "18px 20px",
      animation: `fade-up 0.5s ease ${delay}s both`, position: "relative", overflow: "hidden"
    }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 90, height: 90, borderRadius: "50%", background: `${color}10`, pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 18 }}>{icon}</div>
        {context && (
          <div style={{
            fontFamily: MONO, fontSize: 8, color, background: `${color}14`,
            border: `1px solid ${color}30`, borderRadius: 4,
            padding: "2px 6px", letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap",
          }}>{context}</div>
        )}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>
        <AnimNum to={value} dec={1} suffix={unit} />
      </div>
      {sub && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 5 }}>{sub}</div>}
      {windowLabel && (
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.amber, marginTop: 7, paddingTop: 6, borderTop: `1px dashed ${C.border}`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          ⏱ {windowLabel}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, subtitle, children, height = 260, delay = 0, accentColor, windowTag }) {
  const ac = accentColor || C.cyan;
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: "20px 16px 20px",
      animation: `fade-up 0.5s ease ${delay}s both`,
      position: "relative", overflow: "visible"
    }}>
      <div style={{ position: "absolute", top: 0, left: 20, right: 20, height: 2, background: `linear-gradient(to right, transparent, ${ac}88, transparent)` }} />
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {windowTag && (
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.amber, background: `${C.amber}12`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>
            ⏱ {windowTag}
          </div>
        )}
      </div>
      <div style={{ height }}>{children}</div>
      <div style={{ marginTop: 10 }}>
        <PlotSummary inline title={title} subtitle={subtitle} accent={ac}>{children}</PlotSummary>
      </div>
    </div>
  );
}

/* Liquid Recovery is not shown in the Overview forecast (still shown on GAPL / PAGL / Gas Lift) */
const OVERVIEW_FORECAST_HIDE = ["LiquidVolume_bbl", "total_liquid_recovery_bbl"];

export default function OverviewPage({ summary, timeseries, tsData, tsCols, arrStats, scatterData, liveIdx, currentPoint, COL_COLORS, TTip }) {
  const { isDark } = useTheme();         
  applyTheme(getColors(isDark));          
  const { selectedWell } = useWell();

  /* ───────────── TIME WINDOW STATE ───────────── */
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });

  const stamped = useMemo(() => tsData.map(d => ({ ...d, _t: parseT(d.time) })), [tsData]);
  const tMin = useMemo(() => { const v = stamped.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [stamped]);
  const tMax = useMemo(() => { const v = stamped.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [stamped]);

  // rows for a given window definition (presets are measured back from the LATEST cycle in the data, not from "now")
  const rowsFor = (w) => {
    if (w.mode === "all" || tMax == null) return stamped;
    if (w.mode === "cyc30") return stamped.slice(-30);
    if (w.mode === "custom") {
      const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
      const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
      return stamped.filter(d => d._t != null && d._t >= f && d._t <= t);
    }
    const p = PRESETS.find(x => x.id === w.mode);
    return stamped.filter(d => d._t != null && d._t >= tMax - p.ms);
  };

  const view = useMemo(() => rowsFor(win), [stamped, win, tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, rowsFor({ mode: p.id }).length])), [stamped, tMax]);   // eslint-disable-line react-hooks/exhaustive-deps

  const isAll   = win.mode === "all";
  const vStart  = view.length ? view[0]._t : null;
  const vEnd    = view.length ? view[view.length - 1]._t : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tag     = hasTime ? `${fmtD(vStart)} → ${fmtD(vEnd)} · ${view.length} cyc` : `${view.length} cyc`;
  const cardTag = hasTime ? `${fmtD(vStart)} → ${fmtD(vEnd)}` : null;
  const tooFew  = view.length < MIN_POINTS;

  // stat cards: "ALL" keeps the backend summary exactly as before; any other window is computed from the cycles inside it
  const S = useMemo(() => {
    if (isAll) return summary;
    const o = {};
    ["CsgAtOpen", "TbgAtOpen", "ArrivalVelocity", "ArrivalMinute", "VentMin"].forEach(c => { o[c] = statsOf(view, c); });
    o.CycleSerialNumber = { count: view.length };
    return o;
  }, [isAll, summary, view]);
  const velMean = isAll ? arrStats?.mean : (statsOf(view, "ArrivalVelocity")?.mean != null ? Number(statsOf(view, "ArrivalVelocity").mean.toFixed(1)) : null);

  // scatter: rows carry `index` (added in overview_routes.py); keep only the cycles inside the window
  const scatterRows = useMemo(() => {
    const rows = scatterData?.data || [];
    if (isAll || !rows.length || rows[0].index == null) return rows;
    const keep = new Set(view.map(d => d.index));
    return rows.filter(d => keep.has(d.index));
  }, [scatterData, view, isAll]);

  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <>
      {/* ───────────── DATA WINDOW BAR ───────────── */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px", marginBottom: 18, animation: "fade-up 0.5s ease 0s both" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
          <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
            {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
          </span>
          {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
            {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
          </span>}
          <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
            {view.length} of {stamped.length} cycles
          </span>
          {latestAgo != null && (
            <span title="Presets are measured back from the newest cycle in the data, not from today's date"
              style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
              Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
            </span>
          )}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          {PRESETS.map(p => {
            const n = presetCounts[p.id];
            const disabled = p.id !== "all" && n < MIN_POINTS;
            return (
              <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
                title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window · too few to plot a trend. Try a wider window.` : `${n} cycles`}
                style={btn(win.mode === p.id, disabled)}>
                {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
              </button>
            );
          })}
          <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
            style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
          {win.mode === "custom" && (
            <>
              <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
                onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput}/>
              <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
              <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
                onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput}/>
            </>
          )}
        </div>
        {tooFew && (
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
            ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range · widen the window to see a trend.
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: 14, marginBottom: 24 }}>
        <StatCard label="Casing Pressure" value={S?.CsgAtOpen?.mean} unit=" psi" color={C.cyan} context="Avg"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><circle cx="12" cy="12" r="2"/></svg>}
          sub={`Max: ${S?.CsgAtOpen?.max?.toFixed(1) ?? "·"} psi`} delay={0} windowLabel={cardTag}/>
        <StatCard label="Tubing Pressure" value={S?.TbgAtOpen?.mean} unit=" psi" color={C.teal} context="Avg"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z"/><path d="M5 14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1z"/></svg>}
          sub={`Min: ${S?.TbgAtOpen?.min?.toFixed(1) ?? "·"} psi`} delay={0.05} windowLabel={cardTag}/>
        <StatCard label="Arrival Velocity" value={S?.ArrivalVelocity?.mean} unit=" ft/min" color={C.amber} context="Avg across cycles"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"/><path d="M9.6 4.6A2 2 0 1 1 11 8H2"/><path d="M12.6 19.4A2 2 0 1 0 14 16H2"/></svg>}
          sub={`Std: ${S?.ArrivalVelocity?.std?.toFixed(1) ?? "·"}`} delay={0.1} windowLabel={cardTag}/>
        <StatCard label="Arrival Minute" value={S?.ArrivalMinute?.mean} unit=" min" color={C.pink} context="Avg time to arrive"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
          sub={`Median: ${S?.ArrivalMinute?.median?.toFixed(1) ?? "·"} min`} delay={0.15} windowLabel={cardTag}/>
        <StatCard label="Total Cycles" value={isAll ? (S?.CycleSerialNumber?.count ?? tsData.length) : view.length} unit="" color={C.purple} context={isAll ? "All logged cycles" : "Cycles in window"}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>}
          sub="Logged plunger cycles" delay={0.2} windowLabel={cardTag}/>
        <StatCard label="Vent Minutes" value={S?.VentMin?.mean} unit=" min" color={C.blue} context="Avg vent duration"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/><path d="M8 19h8"/><path d="M6 22h12"/></svg>}
          sub={`Max: ${S?.VentMin?.max?.toFixed(1) ?? "·"} min`} delay={0.25} windowLabel={cardTag}/>
      </div>
      {selectedWell && <ForecastPanel endpoint="plunger/forecast" params={{ well: selectedWell, n: 5 }} C={C} MONO={MONO} SANS={SANS} hide={OVERVIEW_FORECAST_HIDE} plotSummary/>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        <ChartCard windowTag={tag} title="Casing & Tubing Pressure" subtitle="Pressure at valve open events" accentColor={C.cyan} height={280} delay={0.1}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={view} margin={CHART_MARGIN_DEFAULT}>
              <defs>
                <linearGradient id="gcsg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.cyan} stopOpacity={0.3}/><stop offset="95%" stopColor={C.cyan} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gtbg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.teal} stopOpacity={0.25}/><stop offset="95%" stopColor={C.teal} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()}/>
              {dateXAxis(view, 5)}
              <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"psi", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Legend {...LEGEND_PROPS}/>
              {tsCols.includes("CsgAtOpen") && <Area type="monotone" dataKey="CsgAtOpen" name="Casing" stroke={C.cyan} fill="url(#gcsg)" strokeWidth={2} dot={false}/>}
              {tsCols.includes("TbgAtOpen") && <Area type="monotone" dataKey="TbgAtOpen" name="Tubing" stroke={C.teal} fill="url(#gtbg)" strokeWidth={2} dot={false}/>}
              {tsCols.includes("LineAtOpen") && <Line type="monotone" dataKey="LineAtOpen" name="Line" stroke={C.blue} strokeWidth={1.5} dot={false} strokeDasharray="4 2"/>}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard windowTag={tag} title="Plunger Arrival · Velocity & Time" subtitle="ft/min and arrival minute per cycle" accentColor={C.amber} height={280} delay={0.15}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={view} margin={CHART_MARGIN_DUAL_Y}>
              <defs>
                <linearGradient id="gvel" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.amber} stopOpacity={0.25}/><stop offset="95%" stopColor={C.amber} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="index" {...CYCLE_XAXIS_PROPS()}/>
              {dateXAxis(view, 5)}
              <YAxis yAxisId="left" tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"ft/min", angle:-90, position:"insideLeft", offset:14, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <YAxis yAxisId="right" orientation="right" tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={{ value:"min", angle:90, position:"insideRight", offset:-4, fill:C.muted, fontSize:9, fontFamily:MONO }}/>
              <Tooltip content={<TTip />} wrapperStyle={{ zIndex:99999, overflow:"visible !important" }}/>
              <Legend {...LEGEND_PROPS}/>
              {tsCols.includes("ArrivalVelocity") && <Area yAxisId="left" type="monotone" dataKey="ArrivalVelocity" name="Velocity (ft/min)" stroke={C.amber} fill="url(#gvel)" strokeWidth={2} dot={false}/>}
              {tsCols.includes("ArrivalMinute") && <Line yAxisId="right" type="monotone" dataKey="ArrivalMinute" name="Arrival Min" stroke={C.pink} strokeWidth={2} dot={false}/>}
              {arrStats && velMean!=null && <ReferenceLine yAxisId="left" y={velMean} stroke={C.amber+"66"} strokeDasharray="6 3" label={{ value:`μ=${velMean}`, fill:C.amber, fontSize:9, fontFamily:MONO, position:"insideTopRight" }}/>}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      
    </>
  );
}